import APIRequester from './APIRequester';
import { APIDefaultHeaders } from '../../typings/APIRequester';
import { Ad, User, Countries } from '../../typings/delivery';

/**
 * @name AtlasSDK
 * @description SDK to make calls directly to Atlas API
 * @class
 */
export default class AtlasSDK extends APIRequester {
  private baseUrl: string;

  private openOauth: string;

  private atlasHeaders: Record<string, string> = {
    'X-Client': 'MWEB',
    'X-Platform-Type': 'mobile-html5',
  };

  public constructor(atlasUrl: string, apiVersion: string, defaultHeaders: APIDefaultHeaders) {
    super(defaultHeaders);
    this.baseUrl = `${atlasUrl}/api/${apiVersion}`;
    this.openOauth = `${atlasUrl}/api/open/oauth`;
  }

  /**
   * @name getAd
   * @method
   * @public
   */
  public async getAd(adId: string): Promise<Ad> {
    return this.fetchAPI(`${this.baseUrl}/offers/${adId}`, 'GET', null, {
      ...this.atlasHeaders,
      'Content-Type': null,
    });
  }

  /**
   * @name login
   * @param user
   * @public
   */
  public async login(
    user: { login: string; password: string },
    atlasConfig: { clientId: string; clientSecret: string },
  ): Promise<string> {
    const frictionUrl = 'https://staging.friction.olxgroup.com';

    const challenge = await fetch(`${frictionUrl}/challenge`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'content-type': 'application/json', origin: 'localhost' },
      body: JSON.stringify({
        action: 'login',
        aud: 'atlas',
        actor: {
          username: user.login,
        },
        scene: {
          origin: 'pl.stg.eu.olx.org',
        },
      }),
    });
    const { context } = await challenge.json();

    const exchange = await fetch(`${frictionUrl}/exchange`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'content-type': 'application/json', origin: 'localhost' },
      body: JSON.stringify({
        context,
        response: '',
      }),
    });
    const { token } = await exchange.json();

    const res = await fetch(`${this.openOauth}/token`, {
      credentials: 'omit',
      method: 'POST',
      mode: 'cors',
      headers: { 'content-type': 'application/json', ...this.atlasHeaders },
      body: JSON.stringify({
        username: user.login,
        password: user.password,
        friction_token: token,
        grant_type: 'password',
        scope: 'i2 read write v2',
        client_id: atlasConfig.clientId,
        client_secret: atlasConfig.clientSecret,
      }),
    });

    const { access_token: AtlasToken } = await res.json();
    return AtlasToken;
  }

  /**
   * @name getUser
   * @method
   * @public
   */
  public async getUser(): Promise<User> {
    const res = this.fetchAPI(`${this.baseUrl}/users/me`, 'GET', null, {
      ...this.atlasHeaders,
      'Content-Type': null,
    });

    try {
      const userInfo = await res;
      return userInfo.data;
    } catch (e) {
      throw new Error('Could not fetch user id');
    }
  }

  /**
   * @name getCountries
   * @method
   * @public
   */
  public async getCountries(): Promise<Countries> {
    return this.fetchAPI(`${this.baseUrl}/invoicing/countries`, 'GET', null, {
      ...this.atlasHeaders,
      'Content-Type': null,
    });
  }
}
